import React, { useEffect } from 'react';
import { IconArrowRightBold, IconInfoRegular, LogoZdelivery } from '@croquiscom-pvt/zds';
import { Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { useOnceReveal } from '@common/hooks';
import { Typography } from '@common/styles';
import { TouchAccessibilityWrapper, usePdpConfig } from '@domains/pdp-common';
import { EstimateShippingInfo } from '@domains/pdp-common';

import { ShippingZigzinDelayShipmentBannerContainer } from '../containers/ShippingZigzinDelayShipmentBannerContainer';
import { GuideText } from './guide/GuideText';
import { ZigzinArrivalDateText } from './zigzin/ZigzinArrivalDateText';
import { ZigzinDelayDays } from './zigzin/ZigzinDelayDays';

interface ZigzinProductProps {
  isEstimateTimestampExist: boolean;
  displayDeliveryTypeToggleButton: boolean;
  shippingInfo: EstimateShippingInfo;
  itemGuideText: string | null;
  onClickInfoIcon: () => void;
  handleGoToProductDetail: () => void;
  refetchZonlyEstimateShippingInfo: () => void;
  onClickDefaultAddress: () => void;
  shippingDefaultAddressImpressionUBL: () => void;
}

export const ZigzinProduct: React.FC<React.PropsWithChildren<ZigzinProductProps>> = ({
  isEstimateTimestampExist,
  displayDeliveryTypeToggleButton,
  shippingInfo,
  itemGuideText,
  onClickInfoIcon,
  handleGoToProductDetail,
  refetchZonlyEstimateShippingInfo,
  onClickDefaultAddress,
  shippingDefaultAddressImpressionUBL,
}) => {
  const { targetRef, isRevealed } = useOnceReveal();
  const { isProductPreview } = usePdpConfig();
  const { oneday_shipping, zigzin_shipping, default_address_guide, arrival_notice_texts } = shippingInfo;
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isRevealed) {
      shippingDefaultAddressImpressionUBL();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevealed]);

  return (
    <PdpZigzinShippingInfo>
      <Content>
        <Title className={Typography.BODY_14_MEDIUM}>배송</Title>
        <List>
          <TouchAccessibilityWrapper onClick={onClickInfoIcon} data-allow-preview style={{ display: 'inline-block' }}>
            <ZigzinTagWrapper>
              <ZigzinBadge>
                <LogoZdelivery width={67} height={13} />
                {Boolean(zigzin_shipping.islands_mountains_delay_days) && (
                  <ZigzinDelayDays days={zigzin_shipping.islands_mountains_delay_days ?? 0} />
                )}
              </ZigzinBadge>
              {!oneday_shipping && zigzin_shipping?.shipping_fee_text && (
                <span className={`free-delivery-text ${Typography.BODY_14_REGULAR}`}>
                  {zigzin_shipping?.shipping_fee_text}
                </span>
              )}
              {!isProductPreview && <IconInfoRegular width={24} height={24} color='textTertiary' />}
            </ZigzinTagWrapper>
          </TouchAccessibilityWrapper>

          {default_address_guide && (
            <DeliveryAddress ref={targetRef}>
              <LocationIcon src='https://cf.image-farm.s.zigzag.kr/original/mkt/2024/1/9/202401091953550344_43160.webp' />
              <AddressInfo>
                <Region>{default_address_guide.pre_text}</Region>
                <TouchAccessibilityWrapper onClick={onClickDefaultAddress} style={{ display: 'flex' }}>
                  <ButtonRegistration>{default_address_guide.register_default_address_text}</ButtonRegistration>
                  <IconArrowRightBold width={18} height={18} v3Color='gray500' />
                </TouchAccessibilityWrapper>
              </AddressInfo>
            </DeliveryAddress>
          )}
          {oneday_shipping && (
            <CardList>
              <Card>
                <Info>
                  <ShippingText>{oneday_shipping.type_badge_text}</ShippingText>
                  <Schedule>
                    <ArrivalText ref={ref} dangerouslySetInnerHTML={{ __html: oneday_shipping?.estimated_date_text }} />
                    <OrderText>{oneday_shipping.order_cutoff_time_text}</OrderText>
                  </Schedule>
                  {oneday_shipping.guide_badge_text && <Badge>{oneday_shipping.guide_badge_text}</Badge>}
                </Info>
                <ShippingFee variant='Body_13_Medium' color='textTertiary'>
                  {oneday_shipping?.shipping_fee_text}
                </ShippingFee>
              </Card>
              <Card>
                <Info paddingPc={'14px 14px 6px'}>
                  <ShippingText>{zigzin_shipping.type_badge_text}</ShippingText>
                  <Schedule>
                    <ArrivalText dangerouslySetInnerHTML={{ __html: zigzin_shipping?.estimated_date_text }} />
                    <OrderText marginPc={'2px 0 0 0'}>{zigzin_shipping.order_cutoff_time_text}</OrderText>
                    {zigzin_shipping.delivery_delay_text && (
                      <Text variant='Body_13_Medium' v3Color='red200' style={{ marginTop: 9, marginBottom: 2 }}>
                        {zigzin_shipping.delivery_delay_text}
                      </Text>
                    )}
                  </Schedule>
                </Info>
                <ShippingFee variant='Body_13_Medium' color='textTertiary'>
                  {zigzin_shipping?.shipping_fee_text}
                </ShippingFee>
              </Card>
            </CardList>
          )}
          <DetailList className={Typography.BODY_13_MEDIUM}>
            {!oneday_shipping && (
              <>
                <LocationList>
                  <li>
                    <ZigzinArrivalDateText
                      shippingInfo={shippingInfo}
                      isEstimateTimestampExist={isEstimateTimestampExist}
                      updateEstimateInfo={refetchZonlyEstimateShippingInfo}
                    />
                  </li>
                </LocationList>
                {zigzin_shipping.delivery_delay_text && (
                  <Text variant='Body_13_Medium' v3Color='red200' style={{ marginBottom: 12 }}>
                    {zigzin_shipping.delivery_delay_text}
                  </Text>
                )}
                {Boolean(zigzin_shipping.islands_mountains_delay_text) && (
                  <Islands as='div' variant='Body_13_Medium' color='textSecondary'>
                    <LocationIcon src='https://cf.image-farm.s.zigzag.kr/original/mkt/2024/1/9/202401091953550344_43160.webp' />
                    {zigzin_shipping.islands_mountains_delay_text}
                  </Islands>
                )}
              </>
            )}

            {arrival_notice_texts.map((text) => (
              <ShippingNotice
                key={text}
                className={Typography.BODY_13_MEDIUM}
                dangerouslySetInnerHTML={{ __html: text ?? '' }}
              />
            ))}
            {displayDeliveryTypeToggleButton && !isProductPreview && (
              <MatchingShippingNotice onClick={handleGoToProductDetail}>
                *옵션 품절 시 일반배송으로 대체될 수 있으며 가격과
                <br />
                배송정책이 다를 수 있습니다. <span>배송정책 보기</span>
              </MatchingShippingNotice>
            )}
            {itemGuideText && <GuideText text={itemGuideText} />}
          </DetailList>
        </List>
      </Content>
      <ShippingZigzinDelayShipmentBannerContainer />
    </PdpZigzinShippingInfo>
  );
};

const PdpZigzinShippingInfo = styled.div`
  margin: 0 16px;
  padding: 14px 0 16px 0;
`;

const Content = styled.div`
  display: flex;
  align-items: flex-start;
`;
const Title = styled.div`
  margin-right: 8px;
  display: flex;
  align-items: center;
  min-width: 56px;
  height: 20px;
  color: ${vars.color.textTertiary};
`;

const DetailList = styled.div`
  color: ${vars.color.textTertiary};
  margin: 12px 0 0 0;
  font-weight: 400;

  strong {
    font-weight: 600;
    color: ${vars.v3Color.purple500};
  }

  .estimated-time {
    color: ${vars.color.textTertiary};
  }

  &:first-of-type {
    padding: 0;
  }
`;

const List = styled.div`
  width: 100%;
`;

const LocationList = styled.ul`
  margin: 0 0 3px 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    margin: 6px 0 0;

    b {
      color: ${vars.v3Color.purple500};
      font-weight: 500;
    }

    .extra-location-info {
      display: block;
      font-size: 13px;
      padding: 2px 0 0;
      margin: 0;
    }
  }
`;

const ZigzinTagWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 20px;

  .free-delivery-text {
    display: flex;
    align-items: center;

    &::before {
      content: '';
      display: inline-block;
      width: 2px;
      height: 2px;
      border-radius: 2px;
      background: ${vars.v3Color.gray200};
      margin: 0 5px;
    }
  }
`;

const ShippingNotice = styled.p`
  margin: 8px 0 0 0;
  line-height: 18px;
  color: ${vars.color.textTertiary};

  b {
    font-weight: 500;
  }
`;

const MatchingShippingNotice = styled.p`
  margin: 8px 0 0 0;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  color: ${vars.color.textTertiary};

  span {
    text-decoration-line: underline;
    font-weight: 500;
  }
`;

const CardList = styled.div`
  margin: 11px 0 16px 0;
`;

const Card = styled.div`
  margin: 8px 0 0 0;
  border-radius: 6px;
  background-color: ${vars.v3Color.gray50};
`;

const Info = styled.div<{ paddingPc?: string }>`
  padding: ${({ paddingPc }) => (paddingPc ? paddingPc : '14px 14px 7px 14px')};
  display: flex;

  @media screen and (max-width: 374px) {
    padding: 11px 11px 7px 11px;
  }
`;

const ShippingText = styled.div`
  margin: 0 12px 0 0;
  padding: 0 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 30px;
  height: 17px;
  font-size: 10px;
  font-weight: 600;
  color: ${vars.v3Color.staticWhite};
  border-radius: 20px;
  background-color: ${vars.color.purple400};
  white-space: pre;

  @media screen and (max-width: 374px) {
    margin: 0 9px 0 0;
    padding: 2px 5px;
    min-width: auto;
    height: 14px;
    font-size: 8px;
  }
`;

const Badge = styled.div`
  padding: 0 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 17px;
  font-size: 10px;
  font-weight: 600;
  color: ${vars.color.purple400};
  border-radius: 2px;
  background: rgba(110, 82, 255, 0.07);

  @media screen and (max-width: 374px) {
    padding: 0 3px;
    min-width: auto;
    height: 14px;
    font-size: 9px;
  }
`;

const Schedule = styled.div`
  margin: 0 6px 0 0;

  @media screen and (max-width: 374px) {
    margin: 0 5px 0 0;
  }
`;

const ArrivalText = styled.div`
  position: relative;
  top: -0.5px;
  font-size: 14px;
  font-weight: 600;
  color: ${vars.color.textStrong};

  @media screen and (max-width: 374px) {
    top: 0;
    font-size: 11px;
  }
`;

const OrderText = styled.div<{ marginPc?: string }>`
  margin: ${({ marginPc }) => (marginPc ? marginPc : '3px 0 0 0')};
  font-size: 13px;
  font-weight: 400;
  color: ${vars.v3Color.gray900};
  line-height: 16px;

  @media screen and (max-width: 374px) {
    margin: 3px 0 0;
    font-size: 11px;
    line-height: initial;
  }
`;

const ShippingFee = styled(Text)`
  padding: 8px 0 13px 56px;
  display: flex;
  border-top: 1px solid ${vars.v3Color.gray150};
  border-color: rgba(232, 233, 235, 0.5);

  @media screen and (max-width: 374px) {
    padding: 7px 0 11px 44px;
    font-size: 11px;
  }
`;

const LocationIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const DeliveryAddress = styled.div`
  margin: 12px 0;
  position: relative;
  left: -2px;
  display: flex;
  align-items: center;
  color: ${vars.color.textTertiary};

  @media screen and (max-width: 374px) {
    margin: 11px 0;
  }
`;

const ButtonRegistration = styled.div`
  font-size: 14px;
  font-weight: 500;
  text-decoration-line: underline;

  &::before {
    margin: 0 5px;
    position: relative;
    top: -3px;
    content: '';
    display: inline-block;
    width: 2px;
    height: 2px;
    background: #ced4da;
  }
`;

const Region = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const AddressInfo = styled.div`
  display: flex;
`;

const ZigzinBadge = styled.div`
  display: flex;
  align-items: center;
`;

const Islands = styled(Text)`
  margin: 0 0 12px 0;
  position: relative;
  left: -3px;
  display: flex;
  align-items: center;
`;
